<template>
  <div class="space-y-6">
    <div class="flex items-center gap-3">
      <select v-model="runtime" :class="[INPUT, 'w-48']" aria-label="Runtime">
        <option value="">All runtimes</option>
        <option value="single_call">Single call</option>
        <option value="tool_loop">Tool loop</option>
      </select>
      <label class="inline-flex items-center gap-2 text-gray-700">
        <input v-model="showInactive" type="checkbox" class="rounded border-gray-300"> Show not enabled
      </label>
      <span class="text-gray-500">{{ shown.length }} of {{ executors.length }}</span>
      <router-link :to="{ name: 'AdminHermesExecutor', params: { executorKey: 'new' } }" :class="[PRIMARY_BUTTON, 'ml-auto']">New executor</router-link>
    </div>

    <div v-if="error" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">
      <strong>Executors could not be loaded.</strong> {{ error }}
      <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="load">Try again</button>
    </div>

    <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table class="w-full table-fixed divide-y divide-gray-200">
        <colgroup>
          <col style="width:14%"><col style="width:8%"><col style="width:18%"><col style="width:12%"><col style="width:7%"><col style="width:12%">
          <col style="width:10%"><col style="width:6%"><col style="width:6%"><col style="width:7%">
        </colgroup>
        <thead class="bg-gray-50">
          <tr>
            <th :class="TH">Executor</th><th :class="TH">Scope</th><th :class="TH">Runtime</th><th :class="TH">Model</th><th :class="TH">Reasoning</th>
            <th :class="TH">Identity</th><th :class="TH">Grants</th><th :class="[TH, 'text-right']">Runs 30d</th>
            <th :class="[TH, 'text-right']">Spend 30d</th><th :class="TH">Status</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <template v-if="loading">
            <tr v-for="n in 2" :key="n"><td v-for="c in 10" :key="c" :class="TD"><div class="h-4 rounded bg-gray-100" /></td></tr>
          </template>
          <tr v-else-if="!shown.length"><td colspan="10" class="px-3 py-8 text-center text-gray-500">No executors match these filters.</td></tr>
          <tr v-for="e in shown" v-else :key="e.id" class="cursor-pointer hover:bg-gray-50" @click="open(e)">
            <td :class="TD">
              <div class="font-medium">{{ e.name }}</div>
              <div class="text-gray-500 truncate" :title="e.key">{{ e.key }}</div>
            </td>
            <td :class="TD">
              <span :class="['rounded px-2 py-0.5', e.scope === 'platform' ? 'bg-neutral-100 text-gray-900' : 'bg-gray-100 text-gray-800']">{{ e.scope === 'platform' ? 'Platform' : 'Custom' }}</span>
            </td>
            <td :class="TD">
              <div class="font-medium">{{ words(e.runtime) }}</div>
              <div class="text-gray-500">{{ RUNTIME_MEANING[e.runtime] }}</div>
            </td>
            <td :class="TD">
              <div class="truncate" :title="e.model">{{ e.model }}</div>
              <div class="text-gray-500">{{ e.provider || 'Provider unknown' }}</div>
            </td>
            <td :class="TD"><span :class="e.reasoning ? '' : 'text-gray-500'">{{ e.reasoning || 'Default' }}</span></td>
            <td :class="TD">
              <template v-if="e.identity === 'self'">
                <div>Acts as itself</div>
                <div class="text-gray-500">{{ e.service_user_name || 'No service user' }}</div>
              </template>
              <div v-else>Acts as the requester</div>
            </td>
            <td :class="TD">{{ grantSummary(e) }}</td>
            <td :class="[TD, 'text-right tabular-nums']">{{ (e.stats_30d || {}).runs || 0 }}</td>
            <td :class="[TD, 'text-right tabular-nums']">{{ money((e.stats_30d || {}).cost_usd) }}</td>
            <td :class="TD"><HermesBadge :value="e.usable ? 'active' : 'inactive'" :label="!e.is_active ? 'Retired' : (e.enabled ? 'Enabled' : 'Not enabled')" /></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import HermesBadge from './HermesBadge.vue';
import { API, INPUT, PRIMARY_BUTTON, RUNTIME_MEANING, TD, TEXT_BUTTON, TH, errorOf, money, words } from './hermes';

export default {
  name: 'HermesExecutors',
  components: { HermesBadge },
  data() {
    return { executors: [], loading: true, error: '', runtime: '', showInactive: false, INPUT, PRIMARY_BUTTON, RUNTIME_MEANING, TD, TEXT_BUTTON, TH };
  },
  computed: {
    shown() {
      return this.executors.filter((e) => (!this.runtime || e.runtime === this.runtime) && (this.showInactive || e.usable));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    money,
    words,
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await this.$axios.get(`${API}/executors/`);
        this.executors = data;
      } catch (e) {
        this.error = errorOf(e, 'Try again in a moment.');
      } finally {
        this.loading = false;
      }
    },
    grantSummary(e) {
      if (e.identity === 'requester') return "Requester's own · all proposed";
      if (!e.enabled) return '—';
      const direct = e.grants.filter((g) => g.mode === 'direct').length;
      const propose = e.grants.filter((g) => g.mode === 'propose').length;
      if (!direct && !propose) return 'No writes';
      return `${direct} direct · ${propose} propose`;
    },
    open(e) {
      this.$router.push({ name: 'AdminHermesExecutor', params: { executorKey: e.key } });
    },
  },
};
</script>
